import { Decimal } from "decimal.js";

/** Snapshot funcional reproduzido pela versão 1 do motor. */
export const CALCULATION_ENGINE_VERSION = "1.0.0" as const;
export const OBSERVED_MODEL_VERSION = "Fin-Tracks 5.9.20" as const;
const EPSILON = 1e-15;

export type Numeric = number | Decimal;

/** Arredondamento monetário comercial (half-up), retornando Decimal imutável. */
export function money(value: number): Decimal {
  if (!Number.isFinite(value)) throw new RangeError("Valor monetário não finito");
  return new Decimal(value.toString()).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

export function roundMoneyNumber(value: number): number {
  return money(value).toNumber();
}

function observedRound(value: number, decimals = 0): number {
  return new Decimal(value.toString()).toDecimalPlaces(decimals, Decimal.ROUND_HALF_EVEN).toNumber();
}

export function realAnnualRate(
  nominalAnnualPct: number,
  inflationAnnualPct: number,
  roundPercentageDecimals = 2,
): number {
  const nominal = nominalAnnualPct / 100;
  const inflation = inflationAnnualPct / 100;
  if (inflation <= -1 || nominal <= -1) throw new RangeError("Taxas anuais devem ser maiores que -100%");
  const real = (1 + nominal) / (1 + inflation) - 1;
  return observedRound(real * 100, roundPercentageDecimals) / 100;
}

export function nominalAnnualRate(realAnnualPct: number, inflationAnnualPct: number): number {
  return observedRound(((1 + realAnnualPct / 100) * (1 + inflationAnnualPct / 100) - 1) * 100, 2);
}

export function monthlyEffectiveRate(annualRate: number): number {
  if (annualRate <= -1) throw new RangeError("A taxa anual deve ser maior que -100%");
  return (1 + annualRate) ** (1 / 12) - 1;
}

export function annualToMonthlyPct(annualPct: number): number {
  return monthlyEffectiveRate(annualPct / 100) * 100;
}

export function monthlyToAnnualPct(monthlyPct: number): number {
  if (monthlyPct <= -100) throw new RangeError("A taxa mensal deve ser maior que -100%");
  return ((1 + monthlyPct / 100) ** 12 - 1) * 100;
}

function validateMonths(months: number): void {
  if (!Number.isInteger(months) || months < 0) throw new RangeError("A quantidade de meses deve ser um inteiro não negativo");
}

function validateMonth(month: number): void {
  if (!Number.isInteger(month) || month < 1 || month > 12) throw new RangeError("Mês deve estar entre 1 e 12");
}

export function futureValueLump(principal: number, monthlyRate: number, months: number): number {
  validateMonths(months);
  return principal * (1 + monthlyRate) ** months;
}

export function presentValueLump(futureValue: number, monthlyRate: number, months: number): number {
  validateMonths(months);
  return futureValue / (1 + monthlyRate) ** months;
}

export function futureValueAnnuityDue(payment: number, monthlyRate: number, months: number): number {
  validateMonths(months);
  if (months === 0) return 0;
  if (Math.abs(monthlyRate) < EPSILON) return payment * months;
  return payment * (1 + monthlyRate) * (((1 + monthlyRate) ** months - 1) / monthlyRate);
}

export function presentValueAnnuityDue(payment: number, monthlyRate: number, months: number): number {
  validateMonths(months);
  if (months === 0) return 0;
  if (Math.abs(monthlyRate) < EPSILON) return payment * months;
  return payment * (1 + monthlyRate) * ((1 - (1 + monthlyRate) ** -months) / monthlyRate);
}

export function requiredReservePreserve(withdrawal: number, monthlyRate: number): number {
  if (withdrawal <= 0) return 0;
  if (monthlyRate <= 0) return Number.POSITIVE_INFINITY;
  return withdrawal * (1 + monthlyRate) / monthlyRate;
}

export function maxWithdrawalConsumption(reserve: number, monthlyRate: number, months: number): number {
  validateMonths(months);
  if (months === 0) return 0;
  if (Math.abs(monthlyRate) < EPSILON) return reserve / months;
  return reserve / presentValueAnnuityDue(1, monthlyRate, months);
}

export function maxWithdrawalPreserve(reserve: number, monthlyRate: number): number {
  return monthlyRate <= 0 ? 0 : reserve * monthlyRate / (1 + monthlyRate);
}

export function requiredMonthlyDueForGap(gapAtGoal: number, monthlyRate: number, months: number): number {
  if (gapAtGoal <= 0) return 0;
  if (months === 0) return 0;
  const factor = futureValueAnnuityDue(1, monthlyRate, months);
  if (factor <= 0) throw new RangeError("Fator de acumulação inválido");
  return gapAtGoal / factor;
}

export function requiredLumpNowForGap(gapAtGoal: number, monthlyRate: number, months: number): number {
  return gapAtGoal <= 0 ? 0 : presentValueLump(gapAtGoal, monthlyRate, months);
}

export function monthsToRetirement(currentYear: number, currentMonth: number, currentAge: number, retirementAge: number): number {
  validateMonth(currentMonth);
  if (retirementAge < currentAge) return 0;
  const retirementYear = currentYear + retirementAge - currentAge;
  return (retirementYear - currentYear) * 12 + (12 - currentMonth + 1);
}

export function retirementMonths(
  currentYear: number,
  currentMonth: number,
  currentAge: number,
  retirementAge: number,
  lifeExpectancyAge: number,
): number {
  validateMonth(currentMonth);
  if (lifeExpectancyAge < currentAge) return 0;
  const retirementYear = currentYear + retirementAge - currentAge;
  const lifeYear = currentYear + lifeExpectancyAge - currentAge;
  if (retirementYear < currentYear) return (lifeYear - currentYear) * 12 + (12 - currentMonth + 1);
  return Math.max(0, (lifeYear - retirementYear) * 12);
}

export interface RetirementInput {
  currentReserve: number;
  plannedMonthlyContribution: number;
  desiredMonthlyWithdrawal: number;
  nominalAnnualPct: number;
  inflationAnnualPct: number;
  monthsToRetirement: number;
  retirementMonths: number;
  postRetirementNominalAnnualPct?: number | null;
  postRetirementInflationAnnualPct?: number | null;
}

export interface StrategyResult {
  projectedReserve: Decimal;
  requiredReserve: Decimal;
  additionalMonthly: Decimal;
  additionalLumpNow: Decimal;
  maxWithdrawalWithoutExtra: Decimal;
  isPerpetual?: boolean;
}

export interface RetirementResult {
  realAnnualPct: number;
  realMonthlyPct: number;
  consume: StrategyResult;
  preserve: StrategyResult;
  postRetirementRealAnnualPct: number;
}

function strategy(projected: number, required: number, monthlyRate: number, monthsToGoal: number, maxWithdrawal: number): StrategyResult {
  const gap = Math.max(0, required - projected);
  return {
    projectedReserve: money(projected),
    requiredReserve: money(required),
    additionalMonthly: money(requiredMonthlyDueForGap(gap, monthlyRate, monthsToGoal)),
    additionalLumpNow: money(requiredLumpNowForGap(gap, monthlyRate, monthsToGoal)),
    maxWithdrawalWithoutExtra: money(maxWithdrawal),
  };
}

export function analyzeRetirement(data: RetirementInput): RetirementResult {
  const preReal = realAnnualRate(data.nominalAnnualPct, data.inflationAnnualPct);
  const postNominal = data.postRetirementNominalAnnualPct ?? data.nominalAnnualPct;
  const postInflation = data.postRetirementInflationAnnualPct ?? data.inflationAnnualPct;
  const postReal = realAnnualRate(postNominal, postInflation);
  const preMonthly = monthlyEffectiveRate(preReal);
  const postMonthly = monthlyEffectiveRate(postReal);
  const projected = futureValueLump(data.currentReserve, preMonthly, data.monthsToRetirement)
    + futureValueAnnuityDue(data.plannedMonthlyContribution, preMonthly, data.monthsToRetirement);
  const consumeRequired = presentValueAnnuityDue(data.desiredMonthlyWithdrawal, postMonthly, data.retirementMonths);
  const preserveRequired = requiredReservePreserve(data.desiredMonthlyWithdrawal, postMonthly);
  return {
    realAnnualPct: observedRound(preReal * 100, 2),
    realMonthlyPct: preMonthly * 100,
    postRetirementRealAnnualPct: observedRound(postReal * 100, 2),
    consume: strategy(projected, consumeRequired, preMonthly, data.monthsToRetirement,
      maxWithdrawalConsumption(projected, postMonthly, data.retirementMonths)),
    preserve: strategy(projected, preserveRequired, preMonthly, data.monthsToRetirement,
      maxWithdrawalPreserve(projected, postMonthly)),
  };
}

export interface MonthlyCashflowRuleInput {
  startAge: number;
  endAge: number;
  monthlyContribution?: number;
  monthlyWithdrawal?: number;
  contributionPercentStep?: number;
  contributionFixedStep?: number;
  contributionStepYears?: number;
  withdrawalPercentStep?: number;
  withdrawalFixedStep?: number;
  withdrawalStepYears?: number;
  note?: string;
}

export class MonthlyCashflowRule {
  readonly startAge: number;
  readonly endAge: number;
  readonly monthlyContribution: number;
  readonly monthlyWithdrawal: number;
  readonly contributionPercentStep: number;
  readonly contributionFixedStep: number;
  readonly contributionStepYears: number;
  readonly withdrawalPercentStep: number;
  readonly withdrawalFixedStep: number;
  readonly withdrawalStepYears: number;
  readonly note: string;

  constructor(input: MonthlyCashflowRuleInput) {
    this.startAge = input.startAge;
    this.endAge = input.endAge;
    this.monthlyContribution = input.monthlyContribution ?? 0;
    this.monthlyWithdrawal = input.monthlyWithdrawal ?? 0;
    this.contributionPercentStep = input.contributionPercentStep ?? 0;
    this.contributionFixedStep = input.contributionFixedStep ?? 0;
    this.contributionStepYears = input.contributionStepYears ?? 0;
    this.withdrawalPercentStep = input.withdrawalPercentStep ?? 0;
    this.withdrawalFixedStep = input.withdrawalFixedStep ?? 0;
    this.withdrawalStepYears = input.withdrawalStepYears ?? 0;
    this.note = input.note ?? "";
  }

  amountsAtAge(age: number): readonly [number, number] {
    if (age < this.startAge || age > this.endAge) return [0, 0];
    let contribution = this.monthlyContribution;
    let withdrawal = this.monthlyWithdrawal;
    if (this.contributionStepYears > 0) {
      const steps = Math.floor((age - this.startAge) / this.contributionStepYears);
      if (this.contributionPercentStep !== 0) contribution *= (1 + this.contributionPercentStep / 100) ** steps;
      else if (this.contributionFixedStep !== 0) contribution += this.contributionFixedStep * steps;
    }
    if (this.withdrawalStepYears > 0) {
      const steps = Math.floor((age - this.startAge) / this.withdrawalStepYears);
      if (this.withdrawalPercentStep !== 0) withdrawal *= (1 + this.withdrawalPercentStep / 100) ** steps;
      else if (this.withdrawalFixedStep !== 0) withdrawal += this.withdrawalFixedStep * steps;
    }
    return [roundMoneyNumber(contribution), roundMoneyNumber(withdrawal)];
  }
}

export interface AnnualCashflowRuleInput {
  age: number;
  endAge?: number | null;
  month?: number | null;
  annualContribution?: number;
  annualWithdrawal?: number;
  correctForInflation?: boolean;
  note?: string;
}

export class AnnualCashflowRule {
  readonly age: number;
  readonly endAge: number | null;
  readonly month: number | null;
  readonly annualContribution: number;
  readonly annualWithdrawal: number;
  readonly correctForInflation: boolean;
  readonly note: string;

  constructor(input: AnnualCashflowRuleInput) {
    this.age = input.age;
    this.endAge = input.endAge ?? null;
    this.month = input.month ?? null;
    this.annualContribution = input.annualContribution ?? 0;
    this.annualWithdrawal = input.annualWithdrawal ?? 0;
    this.correctForInflation = input.correctForInflation ?? true;
    this.note = input.note ?? "";
  }

  applies(age: number, month: number, currentMonth: number): boolean {
    const endAge = this.endAge ?? this.age;
    if (age < this.age || age > endAge) return false;
    const eventMonth = this.month ?? (age === this.age ? currentMonth : 1);
    return month === eventMonth;
  }
}

export interface CalendarRetirementInput {
  currentYear: number;
  currentMonth: number;
  currentAge: number;
  retirementAge: number;
  lifeExpectancyAge: number;
  currentReserve: number;
  nominalAnnualPct: number;
  inflationAnnualPct: number;
  plannedMonthlyContributionBeforeRetirement?: number;
  plannedMonthlyWithdrawalBeforeRetirement?: number;
  plannedMonthlyContributionAfterRetirement?: number;
  desiredMonthlyWithdrawalAfterRetirement?: number;
  differentiatePostRetirementRates?: boolean;
  postRetirementNominalAnnualPct?: number | null;
  postRetirementInflationAnnualPct?: number | null;
  monthlyRules?: readonly MonthlyCashflowRule[];
  annualRules?: readonly AnnualCashflowRule[];
}

export interface EvolutionRow {
  year: number;
  age: number;
  openingReserve: Decimal;
  monthlyContribution: Decimal;
  monthlyWithdrawal: Decimal;
  annualNetCashflow: Decimal;
  averageMonthlyReturn: Decimal;
  closingReserve: Decimal;
  monthsProcessed: number;
}

export interface CalendarResult {
  engineVersion: typeof CALCULATION_ENGINE_VERSION;
  realAnnualPct: number;
  realMonthlyPct: number;
  postRetirementRealAnnualPct: number;
  monthsToRetirement: number;
  retirementMonths: number;
  retirementReserve: Decimal;
  finalReserve: Decimal;
  totalBaseContributionsToRetirement: Decimal;
  consume: StrategyResult;
  preserve: StrategyResult;
  evolution: readonly EvolutionRow[];
}

export interface SerializedStrategyResult {
  projectedReserve: string;
  requiredReserve: string;
  additionalMonthly: string;
  additionalLumpNow: string;
  maxWithdrawalWithoutExtra: string;
  isPerpetual?: boolean;
}

export interface SerializedEvolutionRow {
  year: number;
  age: number;
  openingReserve: string;
  monthlyContribution: string;
  monthlyWithdrawal: string;
  annualNetCashflow: string;
  averageMonthlyReturn: string;
  closingReserve: string;
  monthsProcessed: number;
}

export interface SerializedCalendarResult {
  engineVersion: typeof CALCULATION_ENGINE_VERSION;
  realAnnualPct: number;
  postRetirementRealAnnualPct: number;
  realMonthlyPct: number;
  monthsToRetirement: number;
  retirementMonths: number;
  retirementReserve: string;
  finalReserve: string;
  totalBaseContributionsToRetirement: string;
  consume: SerializedStrategyResult;
  preserve: SerializedStrategyResult;
  evolution: SerializedEvolutionRow[];
}

function validateCalendar(data: CalendarRetirementInput): void {
  validateMonth(data.currentMonth);
  if (data.retirementAge < 0 || data.lifeExpectancyAge < 0) throw new RangeError("Idades não podem ser negativas");
  if (data.lifeExpectancyAge < data.currentAge) throw new RangeError("Expectativa de vida anterior à idade atual");
  if (data.retirementAge > data.lifeExpectancyAge) throw new RangeError("Aposentadoria posterior à expectativa de vida");
  for (const rule of data.monthlyRules ?? []) {
    if (rule.endAge < rule.startAge) throw new RangeError("Faixa etária mensal inválida");
  }
  for (const rule of data.annualRules ?? []) {
    if (rule.month !== null) validateMonth(rule.month);
    if (rule.endAge !== null && rule.endAge < rule.age) throw new RangeError("Faixa etária anual inválida");
  }
}

export function simulateCalendar(data: CalendarRetirementInput): CalendarResult {
  validateCalendar(data);
  const preReal = realAnnualRate(data.nominalAnnualPct, data.inflationAnnualPct);
  let postNominal = data.nominalAnnualPct;
  let postInflation = data.inflationAnnualPct;
  if (data.differentiatePostRetirementRates) {
    postNominal = data.postRetirementNominalAnnualPct ?? postNominal;
    postInflation = data.postRetirementInflationAnnualPct ?? postInflation;
  }
  const postReal = realAnnualRate(postNominal, postInflation);
  const preMonthly = monthlyEffectiveRate(preReal);
  const postMonthly = monthlyEffectiveRate(postReal);
  const retirementYear = data.currentYear + data.retirementAge - data.currentAge;
  const finalYear = data.currentYear + data.lifeExpectancyAge - data.currentAge;
  let balance = data.currentReserve;
  let retirementReserve: number | null = retirementYear < data.currentYear ? roundMoneyNumber(balance) : null;
  let totalBase = 0;
  const rows: EvolutionRow[] = [];
  const monthlyRules = data.monthlyRules ?? [];
  const annualRules = data.annualRules ?? [];
  const beforeContribution = data.plannedMonthlyContributionBeforeRetirement ?? 0;
  const beforeWithdrawal = data.plannedMonthlyWithdrawalBeforeRetirement ?? 0;
  const afterContribution = data.plannedMonthlyContributionAfterRetirement ?? 0;
  const afterWithdrawal = data.desiredMonthlyWithdrawalAfterRetirement ?? 0;

  for (let year = data.currentYear; year <= finalYear; year += 1) {
    const age = data.currentAge + year - data.currentYear;
    const firstMonth = year === data.currentYear ? data.currentMonth : 1;
    const opening = roundMoneyNumber(balance);
    let annualNet = 0;
    let returnTotal = 0;
    let shownContribution = 0;
    let shownWithdrawal = 0;
    const monthsProcessed = 12 - firstMonth + 1;
    for (let month = firstMonth; month <= 12; month += 1) {
      let contribution = year <= retirementYear ? beforeContribution : afterContribution;
      let withdrawal = year <= retirementYear ? beforeWithdrawal : afterWithdrawal;
      for (const rule of monthlyRules) {
        const [addContribution, addWithdrawal] = rule.amountsAtAge(age);
        contribution += addContribution;
        withdrawal += addWithdrawal;
      }
      let annualMonthNet = 0;
      for (const rule of annualRules) {
        if (rule.applies(age, month, firstMonth)) {
          const yearsFromStart = year - data.currentYear;
          const multiplier = rule.correctForInflation ? (1 + data.inflationAnnualPct / 100) ** yearsFromStart : 1;
          annualMonthNet += roundMoneyNumber((rule.annualContribution - rule.annualWithdrawal) * multiplier);
        }
      }
      annualNet += annualMonthNet;
      balance += annualMonthNet + contribution - withdrawal;
      const rate = year > retirementYear ? postMonthly : preMonthly;
      const monthlyReturn = balance * rate;
      returnTotal += monthlyReturn;
      balance += monthlyReturn;
      shownContribution = contribution;
      shownWithdrawal = withdrawal;
      if (year <= retirementYear) totalBase += beforeContribution;
    }
    balance = roundMoneyNumber(balance);
    rows.push({
      year,
      age,
      openingReserve: money(opening),
      monthlyContribution: money(shownContribution),
      monthlyWithdrawal: money(shownWithdrawal),
      annualNetCashflow: money(annualNet),
      averageMonthlyReturn: money(returnTotal / monthsProcessed),
      closingReserve: money(balance),
      monthsProcessed,
    });
    if (year === retirementYear) retirementReserve = balance;
  }

  retirementReserve ??= data.currentReserve;
  const nPre = monthsToRetirement(data.currentYear, data.currentMonth, data.currentAge, data.retirementAge);
  const nPost = retirementMonths(data.currentYear, data.currentMonth, data.currentAge, data.retirementAge, data.lifeExpectancyAge);
  const desired = afterWithdrawal;
  const consumeRequired = presentValueAnnuityDue(desired, postMonthly, nPost);
  const preserveRequired = requiredReservePreserve(desired, postMonthly);
  return {
    engineVersion: CALCULATION_ENGINE_VERSION,
    realAnnualPct: observedRound(preReal * 100, 2),
    realMonthlyPct: preMonthly * 100,
    postRetirementRealAnnualPct: observedRound(postReal * 100, 2),
    monthsToRetirement: nPre,
    retirementMonths: nPost,
    retirementReserve: money(retirementReserve),
    finalReserve: money(balance),
    totalBaseContributionsToRetirement: money(totalBase),
    consume: strategy(retirementReserve, consumeRequired, preMonthly, nPre,
      maxWithdrawalConsumption(retirementReserve, postMonthly, nPost)),
    preserve: strategy(retirementReserve, preserveRequired, preMonthly, nPre,
      maxWithdrawalPreserve(retirementReserve, postMonthly)),
    evolution: rows,
  };
}

export function serializeCalendarResult(result: CalendarResult): SerializedCalendarResult {
  const serializeStrategy = (value: StrategyResult): SerializedStrategyResult => {
    const base = {
    projectedReserve: value.projectedReserve.toFixed(2),
    requiredReserve: value.requiredReserve.toFixed(2),
    additionalMonthly: value.additionalMonthly.toFixed(2),
    additionalLumpNow: value.additionalLumpNow.toFixed(2),
    maxWithdrawalWithoutExtra: value.maxWithdrawalWithoutExtra.toFixed(2),
    };
    return value.isPerpetual === undefined ? base : { ...base, isPerpetual: value.isPerpetual };
  };
  return {
    engineVersion: result.engineVersion,
    realAnnualPct: result.realAnnualPct,
    postRetirementRealAnnualPct: result.postRetirementRealAnnualPct,
    realMonthlyPct: result.realMonthlyPct,
    monthsToRetirement: result.monthsToRetirement,
    retirementMonths: result.retirementMonths,
    retirementReserve: result.retirementReserve.toFixed(2),
    finalReserve: result.finalReserve.toFixed(2),
    totalBaseContributionsToRetirement: result.totalBaseContributionsToRetirement.toFixed(2),
    consume: serializeStrategy(result.consume),
    preserve: serializeStrategy(result.preserve),
    evolution: result.evolution.map((item) => ({
      year: item.year,
      age: item.age,
      openingReserve: item.openingReserve.toFixed(2),
      monthlyContribution: item.monthlyContribution.toFixed(2),
      monthlyWithdrawal: item.monthlyWithdrawal.toFixed(2),
      annualNetCashflow: item.annualNetCashflow.toFixed(2),
      averageMonthlyReturn: item.averageMonthlyReturn.toFixed(2),
      closingReserve: item.closingReserve.toFixed(2),
      monthsProcessed: item.monthsProcessed,
    })),
  };
}

export function inventoryCost(baseValue: number, legalFeesPct: number, courtCostsPct: number, inheritanceTaxPct: number): Decimal {
  return money(baseValue * (legalFeesPct + courtCostsPct + inheritanceTaxPct) / 100);
}

export interface ProtectionNeedOptions {
  wholeLifeInsuredCapital?: number;
  deductiblePrivatePension?: number;
}

export function protectionNeed(totalExpenses: number, successionNeed: number, currentGuarantees: number, options: ProtectionNeedOptions = {}): Decimal {
  const adjustedGuarantees = currentGuarantees - (options.wholeLifeInsuredCapital ?? 0) - (options.deductiblePrivatePension ?? 0);
  return money(totalExpenses + successionNeed - adjustedGuarantees);
}

/** Alias explícito para consumidores que já recebem despesas e sucessão pré-derivadas. */
export function protectionNeedFromDerived(totalExpenses: number, successionNeed: number, currentGuarantees: number, options: ProtectionNeedOptions = {}): Decimal {
  return protectionNeed(totalExpenses, successionNeed, currentGuarantees, options);
}

export function emergencySuccessionReserve(months: number, monthlyLivingExpenses: number): Decimal {
  if (months < 0) throw new RangeError("Meses não podem ser negativos");
  return money(months * monthlyLivingExpenses);
}

export interface GoalFundingResult {
  accumulatedAtGoal: Decimal;
  pendingAtGoal: Decimal;
  periodicContributionDue: Decimal;
  currentExcess: Decimal;
}

export function goalPeriodicContribution(
  targetValue: number,
  alreadyAccumulated: number,
  annualRealRate: number,
  accumulationMonths: number,
  postAccumulationMonths: number,
  contributionIntervalMonths = 1,
): GoalFundingResult {
  validateMonths(accumulationMonths);
  validateMonths(postAccumulationMonths);
  if (!Number.isInteger(contributionIntervalMonths) || contributionIntervalMonths <= 0) throw new RangeError("Periodicidade deve ser positiva");
  if (accumulationMonths % contributionIntervalMonths !== 0) throw new RangeError("A janela não contém periodicidades inteiras");
  const monthly = monthlyEffectiveRate(annualRealRate);
  const totalMonths = accumulationMonths + postAccumulationMonths;
  const accumulatedAtGoal = futureValueLump(alreadyAccumulated, monthly, totalMonths);
  const pending = targetValue - accumulatedAtGoal;
  if (pending < 0) {
    return {
      accumulatedAtGoal: money(accumulatedAtGoal),
      pendingAtGoal: money(pending),
      periodicContributionDue: money(0),
      currentExcess: money(presentValueLump(-pending, monthly, totalMonths)),
    };
  }
  const periodic = (1 + annualRealRate) ** (contributionIntervalMonths / 12) - 1;
  const contributionCount = accumulationMonths / contributionIntervalMonths;
  const valueAtEnd = presentValueLump(pending, monthly, postAccumulationMonths);
  return {
    accumulatedAtGoal: money(accumulatedAtGoal),
    pendingAtGoal: money(pending),
    periodicContributionDue: money(requiredMonthlyDueForGap(valueAtEnd, periodic, contributionCount)),
    currentExcess: money(0),
  };
}

export interface SACInstallment {
  number: number;
  openingBalanceAfterIndexation: Decimal;
  amortization: Decimal;
  interest: Decimal;
  payment: Decimal;
  closingBalance: Decimal;
}

export function sacSchedule(outstandingBalance: number, installments: number, annualInterestPct: number, annualIndexationPct = 0): readonly SACInstallment[] {
  if (!Number.isInteger(installments) || installments <= 0) throw new RangeError("Quantidade de parcelas deve ser positiva");
  if (outstandingBalance < 0) throw new RangeError("Saldo devedor não pode ser negativo");
  const interestRate = monthlyEffectiveRate(annualInterestPct / 100);
  const indexRate = monthlyEffectiveRate(annualIndexationPct / 100);
  let balance = outstandingBalance;
  let remaining = installments;
  const rows: SACInstallment[] = [];
  for (let number = 1; number <= installments; number += 1) {
    balance = roundMoneyNumber(balance * (1 + indexRate));
    const amortization = balance / remaining;
    const interest = balance * interestRate;
    const payment = roundMoneyNumber(amortization + interest);
    const closing = balance - amortization;
    rows.push({
      number,
      openingBalanceAfterIndexation: money(balance),
      amortization: money(amortization),
      interest: money(interest),
      payment: money(payment),
      closingBalance: money(closing),
    });
    balance = closing;
    remaining -= 1;
  }
  return rows;
}

export interface TaxBracket {
  upperBound: number | null;
  ratePct: number;
  deduction: number;
}

export const OBSERVED_IR_BRACKETS_5_9_20: readonly TaxBracket[] = Object.freeze([
  { upperBound: 29_145.60, ratePct: 0, deduction: 0 },
  { upperBound: 33_919.80, ratePct: 7.5, deduction: 2_185.92 },
  { upperBound: 45_012.60, ratePct: 15, deduction: 4_729.91 },
  { upperBound: 55_976.16, ratePct: 22.5, deduction: 8_105.85 },
  { upperBound: null, ratePct: 27.5, deduction: 10_904.66 },
]);

export function progressiveIncomeTax(annualTaxableBase: number, brackets: readonly TaxBracket[] = OBSERVED_IR_BRACKETS_5_9_20): Decimal {
  if (annualTaxableBase < 0) throw new RangeError("Base tributável não pode ser negativa");
  for (const bracket of brackets) {
    if (bracket.upperBound === null || annualTaxableBase <= bracket.upperBound) {
      return money(Math.max(0, annualTaxableBase * bracket.ratePct / 100 - bracket.deduction));
    }
  }
  throw new RangeError("Tabela tributária inválida");
}

export interface PrivatePensionTaxResult {
  suggestedPgblAnnual: Decimal;
  suggestedPgblMonthly: Decimal;
  taxableBaseWithout: Decimal;
  taxableBaseWith: Decimal;
  taxWithout: Decimal;
  taxWith: Decimal;
  taxBenefit: Decimal;
  recommendedDeclaration: "COMPLETA" | "SIMPLIFICADA";
}

export interface PrivatePensionTaxOptions {
  pgblLimitPct?: number;
  simplifiedDeductionLimit?: number;
  socialSecurityEligible?: boolean;
  brackets?: readonly TaxBracket[];
}

export function privatePensionTaxBenefit(
  annualTaxableIncome: number,
  annualDeductibleExpensesWithoutPension = 0,
  options: PrivatePensionTaxOptions = {},
): PrivatePensionTaxResult {
  if (annualTaxableIncome < 0 || annualDeductibleExpensesWithoutPension < 0) throw new RangeError("Renda e deduções não podem ser negativas");
  const pgblLimitPct = options.pgblLimitPct ?? 12;
  const simplifiedDeductionLimit = options.simplifiedDeductionLimit ?? 17_640;
  const brackets = options.brackets ?? OBSERVED_IR_BRACKETS_5_9_20;
  const suggested = options.socialSecurityEligible === false ? 0 : annualTaxableIncome * pgblLimitPct / 100;
  const baseWithout = Math.max(0, annualTaxableIncome - annualDeductibleExpensesWithoutPension);
  const baseWith = Math.max(0, baseWithout - suggested);
  const taxWithout = progressiveIncomeTax(baseWithout, brackets);
  const taxWith = progressiveIncomeTax(baseWith, brackets);
  return {
    suggestedPgblAnnual: money(suggested),
    suggestedPgblMonthly: money(suggested / 12),
    taxableBaseWithout: money(baseWithout),
    taxableBaseWith: money(baseWith),
    taxWithout,
    taxWith,
    taxBenefit: money(taxWithout.minus(taxWith).toNumber()),
    recommendedDeclaration: annualDeductibleExpensesWithoutPension + suggested > simplifiedDeductionLimit ? "COMPLETA" : "SIMPLIFICADA",
  };
}

export type PrivatePensionProduct = "pgbl" | "vgbl";
export type PrivatePensionTaxRegime = "progressive" | "regressive";
export type SocialSecurity = "none" | "inss" | "rpps";

export interface PrivatePensionProjectionInput {
  product: PrivatePensionProduct;
  taxRegime: PrivatePensionTaxRegime;
  socialSecurity: SocialSecurity;
  annualTaxableIncome: number;
  annualDeductibleExpenses: number;
  currentReserve: number;
  monthlyContribution: number;
  annualReturnPct: number;
  inflationAnnualPct: number;
  accumulationYears: number;
  retirementIncomeYears: number;
}

export interface PrivatePensionProjectionResult {
  suggestedPgblAnnual: Decimal;
  taxBenefit: Decimal;
  grossReserve: Decimal;
  taxableRescue: Decimal;
  estimatedTax: Decimal;
  netReserve: Decimal;
  sustainableMonthlyIncome: Decimal;
  taxRatePct: number;
}

export function regressivePensionTaxRate(holdingYears: number): number {
  if (holdingYears < 0) throw new RangeError("Tempo de permanência não pode ser negativo");
  if (holdingYears < 2) return 35;
  if (holdingYears < 4) return 30;
  if (holdingYears < 6) return 25;
  if (holdingYears < 8) return 20;
  if (holdingYears < 10) return 15;
  return 10;
}

export function privatePensionProjection(data: PrivatePensionProjectionInput): PrivatePensionProjectionResult {
  if (data.annualTaxableIncome < 0 || data.annualDeductibleExpenses < 0 || data.currentReserve < 0 || data.monthlyContribution < 0) {
    throw new RangeError("Valores da previdência não podem ser negativos");
  }
  if (!Number.isInteger(data.accumulationYears) || data.accumulationYears < 0) throw new RangeError("Anos de acumulação inválidos");
  if (!Number.isInteger(data.retirementIncomeYears) || data.retirementIncomeYears <= 0) throw new RangeError("Horizonte de renda inválido");
  if (data.annualReturnPct <= -100 || data.inflationAnnualPct <= -100) throw new RangeError("Taxas devem ser maiores que -100%");
  const socialSecurityEligible = data.socialSecurity !== "none";
  const taxBenefit = data.product === "pgbl"
    ? privatePensionTaxBenefit(data.annualTaxableIncome, data.annualDeductibleExpenses, { socialSecurityEligible })
    : privatePensionTaxBenefit(0, 0, { pgblLimitPct: 0, socialSecurityEligible: false });
  const months = data.accumulationYears * 12;
  const monthlyNominal = monthlyEffectiveRate(data.annualReturnPct / 100);
  const gross = futureValueLump(data.currentReserve, monthlyNominal, months)
    + futureValueAnnuityDue(data.monthlyContribution, monthlyNominal, months);
  const contributions = data.currentReserve + data.monthlyContribution * months;
  const taxable = data.product === "pgbl" ? gross : Math.max(0, gross - contributions);
  let taxRatePct: number;
  let estimatedTax: number;
  if (data.taxRegime === "regressive") {
    taxRatePct = regressivePensionTaxRate(data.accumulationYears);
    estimatedTax = taxable * taxRatePct / 100;
  } else {
    const tax = progressiveIncomeTax(taxable);
    estimatedTax = tax.toNumber();
    taxRatePct = taxable === 0 ? 0 : estimatedTax / taxable * 100;
  }
  const realAnnual = realAnnualRate(data.annualReturnPct, data.inflationAnnualPct);
  const realMonthly = monthlyEffectiveRate(realAnnual);
  const netReserve = Math.max(0, gross - estimatedTax);
  return {
    suggestedPgblAnnual: taxBenefit.suggestedPgblAnnual,
    taxBenefit: taxBenefit.taxBenefit,
    grossReserve: money(gross),
    taxableRescue: money(taxable),
    estimatedTax: money(estimatedTax),
    netReserve: money(netReserve),
    sustainableMonthlyIncome: money(maxWithdrawalConsumption(netReserve, realMonthly, data.retirementIncomeYears * 12)),
    taxRatePct: observedRound(taxRatePct, 2),
  };
}

export interface ConsortiumInput {
  credit: number;
  termMonths: number;
  anticipationFeePct: number;
  administrationFeePct: number;
  reserveFundPct: number;
  monthlyInsurancePct: number;
  installmentReducerPct: number;
  contemplationInstallment: number;
  totalBidPct: number;
  embeddedBidPct: number;
  annualAdjustmentPct?: number;
}

export interface ConsortiumResult {
  anticipationFee: Decimal;
  administrationFee: Decimal;
  reserveFund: Decimal;
  debtBeforePayments: Decimal;
  initialMonthlyInsurance: Decimal;
  installmentWithoutReducer: Decimal;
  installmentBeforeContemplation: Decimal;
  totalBid: Decimal;
  ownFundsBid: Decimal;
  embeddedBid: Decimal;
  availableCredit: Decimal;
  debtAfterContemplation: Decimal;
  installmentAfterContemplation: Decimal;
  shortenedTermInstallment: Decimal;
  shortenedTermMonths: number;
}

export function analyzeConsortium(data: ConsortiumInput): ConsortiumResult {
  if (data.credit < 0 || data.termMonths <= 0) throw new RangeError("Crédito e prazo devem ser positivos");
  if (data.contemplationInstallment < 1 || data.contemplationInstallment > data.termMonths) throw new RangeError("Parcela de contemplação fora do prazo");
  const administration = data.credit * data.administrationFeePct / 100;
  const reserve = data.credit * data.reserveFundPct / 100;
  const anticipation = data.credit * data.anticipationFeePct / 100;
  const debt = data.credit + administration + reserve;
  const baseAmortization = debt / data.termMonths;
  const insuranceInitial = debt * data.monthlyInsurancePct / 100;
  const installmentWithoutReducer = baseAmortization + insuranceInitial;
  const preInstallment = baseAmortization * (1 - data.installmentReducerPct / 100) + insuranceInitial;
  const totalBid = (debt + anticipation) * data.totalBidPct / 100;
  const embedded = data.credit * data.embeddedBidPct / 100;
  const own = totalBid - embedded;
  const remaining = data.termMonths - data.contemplationInstallment;
  const preContemplationInstallments = remaining === 0 ? Math.max(0, data.contemplationInstallment - 1) : data.contemplationInstallment;
  const principalPaidBefore = baseAmortization * (1 - data.installmentReducerPct / 100) * preContemplationInstallments;
  const debtAfter = Math.max(0, debt - principalPaidBefore - totalBid);
  const postInstallment = remaining === 0 ? debtAfter : debtAfter / remaining + debtAfter * data.monthlyInsurancePct / 100;
  const shortenedInstallment = baseAmortization + debtAfter * data.monthlyInsurancePct / 100;
  const shortenedMonths = baseAmortization === 0 ? 0 : observedRound(debtAfter / baseAmortization);
  return {
    anticipationFee: money(anticipation),
    administrationFee: money(administration),
    reserveFund: money(reserve),
    debtBeforePayments: money(debt),
    initialMonthlyInsurance: money(insuranceInitial),
    installmentWithoutReducer: money(installmentWithoutReducer),
    installmentBeforeContemplation: money(preInstallment),
    totalBid: money(totalBid),
    ownFundsBid: money(own),
    embeddedBid: money(embedded),
    availableCredit: money(data.credit - embedded),
    debtAfterContemplation: money(debtAfter),
    installmentAfterContemplation: money(postInstallment),
    shortenedTermInstallment: money(shortenedInstallment),
    shortenedTermMonths: shortenedMonths,
  };
}

export function consortiumPaymentSchedule(data: ConsortiumInput): readonly Decimal[] {
  const result = analyzeConsortium(data);
  const pre = result.installmentBeforeContemplation.toNumber();
  const post = result.installmentAfterContemplation.toNumber();
  const values: Decimal[] = [];
  for (let monthNumber = 1; monthNumber <= data.termMonths; monthNumber += 1) {
    let payment: number;
    if (monthNumber < data.contemplationInstallment || (monthNumber === data.contemplationInstallment && data.contemplationInstallment < data.termMonths)) {
      payment = pre;
      if (monthNumber === 1) payment += result.anticipationFee.toNumber();
      if (monthNumber === data.contemplationInstallment) payment += result.ownFundsBid.toNumber();
    } else {
      const adjustmentCount = Math.floor((monthNumber - 1) / 12);
      payment = post * (1 + (data.annualAdjustmentPct ?? 0) / 100) ** adjustmentCount;
    }
    values.push(money(payment));
  }
  return values;
}

export interface ConsortiumScheduleRow {
  payment: Decimal;
  principal: Decimal;
  interest: Decimal;
  balance: Decimal;
}

/**
 * Converte o calendário de parcelas do consórcio no envelope R5 persistido.
 * Consórcio não é tratado como amortização SAC: a obrigação financeira fica
 * referenciada ao crédito original, sem inventar juros ou amortização mensal.
 */
export function consortiumScheduleRows(payments: readonly Numeric[], credit: number): readonly ConsortiumScheduleRow[] {
  if (!Number.isFinite(credit) || credit < 0) throw new RangeError("Crédito deve ser não negativo");
  return payments.map((payment) => ({ payment: money(asNumber(payment)), principal: money(0), interest: money(0), balance: money(credit) }));
}

export function consortiumScheduleRowsFromInput(data: ConsortiumInput): readonly ConsortiumScheduleRow[] {
  return consortiumScheduleRows(consortiumPaymentSchedule(data), data.credit);
}

export interface FinancingComparisonRow {
  installment: number;
  suggestedPayment: Decimal;
  alternativePayment: Decimal;
  difference: Decimal;
  accumulatedDifference: Decimal;
}

function asNumber(value: Numeric): number {
  return value instanceof Decimal ? value.toNumber() : value;
}

export function compareFinancingSchedules(
  suggested: readonly Numeric[],
  alternative: readonly Numeric[],
  annualOpportunityRatePct: number,
): readonly FinancingComparisonRow[] {
  const length = Math.max(suggested.length, alternative.length);
  const monthly = monthlyEffectiveRate(annualOpportunityRatePct / 100);
  let balance = 0;
  const rows: FinancingComparisonRow[] = [];
  for (let index = 0; index < length; index += 1) {
    const suggestedValue = index < suggested.length ? asNumber(suggested[index]!) : 0;
    const alternativeValue = index < alternative.length ? asNumber(alternative[index]!) : 0;
    const difference = alternativeValue - suggestedValue;
    balance = (balance + difference) * (1 + monthly);
    rows.push({
      installment: index + 1,
      suggestedPayment: money(suggestedValue),
      alternativePayment: money(alternativeValue),
      difference: money(difference),
      accumulatedDifference: money(balance),
    });
  }
  return rows;
}

export interface FinancingAnnualComparisonRow {
  year: number;
  suggestedPayment: Decimal;
  alternativePayment: Decimal;
  difference: Decimal;
  accumulatedDifference: Decimal;
}

export function annualizeFinancingComparison(rows: readonly FinancingComparisonRow[]): readonly FinancingAnnualComparisonRow[] {
  const years = new Map<number, { suggested: number; alternative: number; difference: number; accumulated: number }>();
  for (const row of rows) {
    const year = Math.ceil(row.installment / 12);
    const current = years.get(year) ?? { suggested: 0, alternative: 0, difference: 0, accumulated: 0 };
    current.suggested += row.suggestedPayment.toNumber();
    current.alternative += row.alternativePayment.toNumber();
    current.difference += row.difference.toNumber();
    current.accumulated = row.accumulatedDifference.toNumber();
    years.set(year, current);
  }
  return [...years.entries()].map(([year, value]) => ({
    year,
    suggestedPayment: money(value.suggested),
    alternativePayment: money(value.alternative),
    difference: money(value.difference),
    accumulatedDifference: money(value.accumulated),
  }));
}

export function presentValueSchedule(schedule: readonly Numeric[], annualOpportunityRatePct: number): Decimal {
  const monthly = monthlyEffectiveRate(annualOpportunityRatePct / 100);
  const value = schedule.reduce<number>((total, payment, index) => total + asNumber(payment) / (1 + monthly) ** (index + 1), 0);
  return money(value);
}

export function periodicInternalRateOfReturn(cashflows: readonly number[]): number | null {
  if (cashflows.length < 2) return null;
  const hasPositive = cashflows.some(value => value > 0);
  const hasNegative = cashflows.some(value => value < 0);
  if (!hasPositive || !hasNegative) return null;
  const npv = (rate: number) => cashflows.reduce((total, value, index) => total + value / (1 + rate) ** index, 0);
  let low = -0.9999;
  let high = 10;
  let lowValue = npv(low);
  let highValue = npv(high);
  for (let attempt = 0; attempt < 12 && lowValue * highValue > 0; attempt += 1) {
    high *= 2;
    highValue = npv(high);
  }
  if (lowValue * highValue > 0) return null;
  for (let iteration = 0; iteration < 120; iteration += 1) {
    const middle = (low + high) / 2;
    const middleValue = npv(middle);
    if (Math.abs(middleValue) < 1e-14) return (1 + middle) ** 12 - 1;
    if (lowValue * middleValue <= 0) {
      high = middle;
      highValue = middleValue;
    } else {
      low = middle;
      lowValue = middleValue;
    }
  }
  return (1 + (low + high) / 2) ** 12 - 1;
}

export function periodicInternalRateOfReturnPct(cashflows: readonly number[]): number | null {
  const annualRate = periodicInternalRateOfReturn(cashflows);
  return annualRate === null ? null : annualRate * 100;
}
